import React, { useState } from "react";
import { ArrowRight2, Calendar, Call, Clock, Home, Message } from "iconsax-reactjs";
import { useDealerOverview } from "../hooks/useDealerOverview";
import {
  DayColumns, DayTable, OverviewToolbar, StageMix, StatStrip, StatTile, TrendBars, dayLabel,
} from "./overview";
import { LeadChannelChip, LeadTime, leadActivityDate, leadStatusLabel } from "./DealerLeads";
import { getListingCardInfo } from "../utils/listingDisplay";
import { queueLeadChat } from "../utils/chatShared";
import { PERIODS, changeOf, formatCount, leadActiveAt, leadStage, waitingAge } from "../utils/overviewData";
import "./DealerDashboard.css";

const MINUTE_MS = 60 * 1000;

// The Leads page's channel icons (LeadChannelChip)
const CHANNEL_ICONS = { call: Call, chat: Message, visit: Calendar };

function waitedFor(since, now) {
  const minutes = Math.max(1, Math.floor((now - since) / MINUTE_MS));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day" : `${days} days`;
}

const plural = (n, one, many) => `${formatCount(n)} ${n === 1 ? one : many}`;
const last14 = (series) => series.slice(-14).map((day) => day.count);

/**
 * The dealer's Dashboard: what waits for them, how this period compares
 * with the last, which listings draw demand, and a door from every number to
 * the page that acts on it. It replaces the old Insights page.
 */
export default function DealerDashboard({ user, setActiveTab, unreadChats = 0, onOpenLead }) {
  const [periodId, setPeriodId] = useState("7d");
  const [chartView, setChartView] = useState("chart");
  const {
    status, refreshing, updatedAt, refresh, win, leads, views, listings, top, reels, recentLeads, errors,
  } = useDealerOverview(user?.uid, periodId);

  const period = PERIODS.find((p) => p.id === periodId) || PERIODS[0];
  const loading = status !== "ready";
  const dim = refreshing ? " ov-dim" : "";

  if (status === "error") {
    return (
      <div className="ov-page">
        <div className="card">
          <div className="empty-state">
            <h3>Couldn't load your dashboard</h3>
            <p>Check your connection, then reload the page.</p>
            <button type="button" className="btn-secondary btn-sm" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }

  const oldest = leads?.waiting[0];
  let waitingNote = "All new leads answered";
  if (oldest && oldest.age === "fresh") waitingNote = `Oldest ${waitedFor(oldest.since, win.now)} ago`;
  else if (oldest) waitingNote = `${waitedFor(oldest.since, win.now)} waiting`;

  const busiest = leads?.series.reduce((best, day) => (day.count > (best?.count || 0) ? day : best), null);
  const pipelineTotal = leads ? leads.pipeline.reduce((n, row) => n + row.count, 0) : 0;

  const openChat = (lead) => {
    queueLeadChat(lead);
    setActiveTab("dealer-chat");
  };

  return (
    <div className="ov-page dd-page">
      <OverviewToolbar
        periodId={periodId}
        onPeriodChange={setPeriodId}
        compareText={`Compared with ${period.before}`}
        updatedAt={updatedAt}
        onRefresh={refresh}
        refreshing={refreshing}
      />

      <StatStrip label="Your numbers">
        <StatTile
          label="New leads waiting"
          value={leads?.waiting.length ?? 0}
          loading={loading}
          note={leads ? waitingNote : undefined}
          noteTone={oldest && oldest.age !== "fresh" ? "warning" : "muted"}
          noteIcon={<Clock size={14} variant="Linear" aria-hidden="true" />}
          linkLabel="Open Leads"
          onOpen={() => setActiveTab("dealer-leads")}
        />
        <StatTile
          label="Enquiries"
          value={leads?.current ?? 0}
          loading={loading}
          change={leads ? changeOf(leads.current, leads.previous) : null}
          compareText={period.before}
          trend={leads ? last14(leads.series) : null}
          trendLabel="Enquiries per day, last 14 days"
          note={leads ? `${formatCount(leads.direct)} direct, ${formatCount(leads.nearby)} nearby` : undefined}
          linkLabel="Open Leads"
          onOpen={() => setActiveTab("dealer-leads")}
        />
        <StatTile
          label="Listing views"
          value={errors.views ? "Not loaded" : views?.current ?? 0}
          loading={loading}
          change={views && !errors.views ? changeOf(views.current, views.previous) : null}
          compareText={period.before}
          trend={views && !errors.views ? last14(views.series) : null}
          trendLabel="Listing views per day, last 14 days"
          note={errors.views ? "Views couldn't be loaded" : listings ? `Across ${plural(listings.live, "live listing", "live listings")}` : undefined}
          linkLabel="Open Listings"
          onOpen={() => setActiveTab("dealer-listings")}
        />
        <StatTile
          label="Unread chats"
          value={unreadChats}
          note={unreadChats > 0 ? "Replies waiting in your inbox" : "Inbox is clear"}
          linkLabel="Open Inbox"
          onOpen={() => setActiveTab("dealer-chat")}
        />
      </StatStrip>

      <div className="ov-row">
        <section className={`card ov-card${dim}`} aria-labelledby="dd-trend-title">
          <div className="card-header">
            <h2 className="card-title" id="dd-trend-title">Enquiries and views</h2>
            <div className="ov-head-actions">
              {busiest && <span className="ov-card-meta">Busiest day: {dayLabel(busiest.start)}</span>}
              <div className="segmented" role="group" aria-label="Show as">
                <button type="button" aria-pressed={chartView === "chart"} onClick={() => setChartView("chart")}>Chart</button>
                <button type="button" aria-pressed={chartView === "table"} onClick={() => setChartView("table")}>Table</button>
              </div>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="skeleton dd-skel-chart" />
            ) : chartView === "table" ? (
              <DayTable
                series={leads.series}
                caption="Enquiries and listing views per day"
                columns={[
                  { label: "Enquiries", counts: leads.series.map((day) => day.count) },
                  ...(errors.views ? [] : [{ label: "Listing views", counts: views.series.map((day) => day.count) }]),
                ]}
              />
            ) : (
              <div className="ov-charts">
                <DayColumns
                  series={leads.series}
                  label="Enquiries"
                  unit={(n) => plural(n, "enquiry", "enquiries")}
                  showDates={!!errors.views}
                />
                {errors.views ? (
                  <p className="ov-error">Listing views couldn't be loaded. Refresh to try again.</p>
                ) : (
                  <DayColumns
                    series={views.series}
                    label="Listing views"
                    unit={(n) => plural(n, "view", "views")}
                    showDates
                  />
                )}
                {win.series.length > win.days && <p className="ov-key">Lighter columns are {period.before}.</p>}
              </div>
            )}
          </div>
        </section>

        <section className={`card ov-card${dim}`} aria-labelledby="dd-recent-title">
          <div className="card-header">
            <h2 className="card-title" id="dd-recent-title">Recent leads</h2>
            <button type="button" className="ov-link ov-head-actions" onClick={() => setActiveTab("dealer-leads")}>
              Open Leads
              <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
            </button>
          </div>
          {loading ? (
            <div className="card-body"><div className="skeleton dd-skel-list" /></div>
          ) : recentLeads.length === 0 ? (
            <p className="dd-empty">When someone calls, chats or asks for a visit, the lead shows up here.</p>
          ) : (
            <ul className="dd-leads">
              {recentLeads.map((lead) => {
                const name = lead.tenantName || "Client";
                const since = leadActiveAt(lead);
                const late = leadStage(lead) === "New" && since !== null && waitingAge(since, win.now) !== "fresh";
                const channel = Array.isArray(lead.actions) && lead.actions.length ? lead.actions[0] : lead.type;
                return (
                  <li key={lead.id} className="dd-lead">
                    <div className="dd-lead-main">
                      <div className="dd-lead-top">
                        <button
                          type="button"
                          className="dd-lead-name"
                          onClick={() => onOpenLead?.({ leadId: lead.id, view: lead.leadType === "nearby" ? "nearby" : "direct" })}
                        >
                          {name}
                        </button>
                        {late ? (
                          <span className="chip chip-warning">
                            <Clock size={12} variant="Linear" aria-hidden="true" />
                            {waitedFor(since, win.now)} waiting
                          </span>
                        ) : (
                          <LeadTime date={leadActivityDate(lead)} />
                        )}
                      </div>
                      <div className="dd-lead-meta">
                        <LeadChannelChip type={channel} />
                        <span className="dd-lead-listing">{lead.propertyTitle || "Direct enquiry"}</span>
                      </div>
                    </div>
                    <div className="dd-lead-actions">
                      {lead.tenantPhone && (
                        <a className="btn-icon dd-act" href={`tel:${lead.tenantPhone}`} aria-label={`Call ${name}`}>
                          <Call size={18} variant="Linear" aria-hidden="true" />
                        </a>
                      )}
                      <button type="button" className="btn-icon dd-act" onClick={() => openChat(lead)} aria-label={`Chat with ${name}`}>
                        <Message size={18} variant="Linear" aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {reels && (
            <div className="dd-reels">
              <span className="dd-reels-label">Reels, all time</span>
              <span className="dd-reels-stats">
                <span><strong>{formatCount(reels.views)}</strong> views</span>
                <span><strong>{formatCount(reels.saves)}</strong> saves</span>
                <span><strong>{formatCount(reels.enquiries)}</strong> enquiries</span>
              </span>
              <button type="button" className="ov-link" onClick={() => setActiveTab("dealer-reels")}>
                Open Reels
                <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
              </button>
            </div>
          )}
        </section>
      </div>

      <div className="ov-row">
        <section className={`card ov-card${dim}`} aria-labelledby="dd-top-title">
          <div className="card-header">
            <h2 className="card-title" id="dd-top-title">Top listings this period</h2>
            <button type="button" className="ov-link ov-head-actions" onClick={() => setActiveTab("dealer-listings")}>
              Open Listings
              <ArrowRight2 size={14} variant="Linear" aria-hidden="true" />
            </button>
          </div>
          {loading ? (
            <div className="card-body"><div className="skeleton dd-skel-list" /></div>
          ) : top.length === 0 ? (
            <p className="dd-empty">No enquiries or views on your listings in this period yet.</p>
          ) : (
            <div className="dd-table-wrap">
              <table className="table dd-top">
                <thead>
                  <tr>
                    <th scope="col">Listing</th>
                    <th scope="col">Price</th>
                    <th scope="col" className="num">Enquiries</th>
                    <th scope="col" className="num dd-col-views">Views</th>
                    <th scope="col" className="dd-col-trend">Last 14 days</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map(({ listing, enquiries, views: listingViews, demand }) => {
                    const info = getListingCardInfo(listing);
                    const cover = listing.images?.[0];
                    const place = [listing.locality, listing.city].filter(Boolean).join(", ");
                    return (
                      <tr key={listing.id}>
                        <td>
                          <div className="dd-listing">
                            <span className="dd-thumb" aria-hidden="true">
                              <Home size={18} variant="Linear" />
                              {cover && (
                                <img src={cover} alt="" onError={(e) => { e.currentTarget.hidden = true; }} />
                              )}
                            </span>
                            <span className="dd-listing-text">
                              <button
                                type="button"
                                className="dd-listing-title"
                                onClick={() => setActiveTab("dealer-listings")}
                              >
                                {listing.title || "Untitled listing"}
                              </button>
                              {place && <span className="dd-listing-place">{place}</span>}
                            </span>
                          </div>
                        </td>
                        <td className="dd-price">
                          {info.priceLabel}
                          {info.priceUnit && <span className="dd-price-unit"> {info.priceUnit}</span>}
                        </td>
                        <td className="num">{formatCount(enquiries)}</td>
                        <td className="num dd-col-views">{formatCount(listingViews)}</td>
                        <td className="dd-col-trend">
                          <TrendBars counts={demand} label="Enquiries, last 14 days" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={`card ov-card${dim}`} aria-labelledby="dd-pipeline-title">
          <div className="card-header">
            <h2 className="card-title" id="dd-pipeline-title">Lead pipeline</h2>
            {leads && <span className="ov-card-meta">{plural(pipelineTotal, "lead", "leads")}</span>}
          </div>
          <div className="card-body dd-pipeline">
            {loading ? (
              <div className="skeleton dd-skel-list" />
            ) : (
              <>
                <StageMix
                  label="Leads by stage"
                  rows={leads.pipeline.map((row) => ({
                    id: row.stage,
                    label: leadStatusLabel(row.stage),
                    count: row.count,
                    muted: row.stage === "Closed",
                  }))}
                  onOpen={() => setActiveTab("dealer-leads")}
                />
                <div className="dd-channels">
                  <div className="dd-channels-head">
                    <h3 className="dd-subhead">How leads reached you</h3>
                    <span className="ov-card-meta">This period</span>
                  </div>
                  <ul className="dd-channel-stats" aria-label="Leads by channel this period">
                    {leads.channels.map((channel) => {
                      const Icon = CHANNEL_ICONS[channel.id];
                      return (
                        <li key={channel.id}>
                          <span className="dd-channel-value">
                            {Icon && <Icon size={16} variant="Linear" aria-hidden="true" />}
                            {formatCount(channel.count)}
                          </span>
                          <span className="dd-channel-label">{channel.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
